'use client';

/**
 * useOfflineAfterGrace — "are we genuinely offline?", debounced.
 *
 * `connectionStatus !== CONNECTED` is true during the INITIAL handshake too,
 * so using it raw makes a normal page load announce a problem that doesn't
 * exist. On the embedded panel that showed as "Reconnecting…" plus the alarm
 * ribbon on first open, before a socket had ever been attempted — alarming,
 * and untrue.
 *
 * The grace period is the fix the full-page ConnectionStatusBadge already
 * used; this hook is that logic extracted so both surfaces share one
 * implementation and one number rather than drifting.
 */

import { useEffect, useState } from 'react';
import { ConnectionStatus } from '@/types/chat';

/**
 * Grace period before a non-connected status surfaces in the UI. The initial
 * WebSocket handshake (and quick reconnect blips) resolve well within this,
 * so a normal load shows nothing at all; a genuine outage still surfaces.
 */
export const CONNECTION_GRACE_MS = 1500;

export function useOfflineAfterGrace(status: ConnectionStatus): boolean {
  // Keyed on the connected/not-connected BOOLEAN, not the status value, so
  // label changes within an outage (CONNECTING → DISCONNECTED) don't restart
  // the grace timer — once visible, the indicator tracks the live label.
  const offline = status !== ConnectionStatus.CONNECTED;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!offline) {
      setVisible(false);
      return;
    }
    const timer = window.setTimeout(
      () => setVisible(true),
      CONNECTION_GRACE_MS
    );
    return () => window.clearTimeout(timer);
  }, [offline]);

  return offline && visible;
}
